import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useCreateFeatureType, useGetFeatureType, useUpdateFeatureType } from '../../hooks/api/use-feature-types.hook';
import { FeatureTypeApiModel } from '../../../../types/models/feature-type.model';
import { LoadingIndicator } from '../LoadingIndicator';
import { FeatureTypeForm } from './FeatureTypeForm';

export const AddEditFeatureType = () => {
  const navigate = useNavigate();
  const { featureTypeId } = useParams<{ featureTypeId: string }>();

  const {
    featureType,
    loading: loadingFeatureType,
    error: gettingError,
  } = useGetFeatureType<FeatureTypeApiModel>({ featureTypeId: Number(featureTypeId) });
  const [createFeatureType, { loading: creatingFeatureType, error: createError }] = useCreateFeatureType();
  const [updateFeatureType, { loading: updatingFeatureType, error: updateError }] = useUpdateFeatureType();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FeatureTypeForm>({ resolver: classValidatorResolver(FeatureTypeForm), values: featureType });

  const onSubmit: SubmitHandler<FeatureTypeForm> = async (values) => {
    await (featureType ? updateFeatureType({ ...values, featureTypeId: featureType.id }) : createFeatureType(values));
    navigate('..');
  };

  const renderContent = () => {
    if (gettingError) {
      return <Alert severity="error">{gettingError?.response?.data.message}</Alert>;
    }

    return (
      <>
        <TextField
          {...register('name')}
          variant="outlined"
          sx={{ mt: '1rem' }}
          disabled={creatingFeatureType || updatingFeatureType}
          error={Boolean(errors.name)}
          helperText={errors.name?.message}
          label="Name"
          margin="dense"
          size="small"
          fullWidth
        />
        <TextField
          {...register('value')}
          variant="outlined"
          sx={{ mt: '1rem' }}
          disabled={creatingFeatureType || updatingFeatureType}
          error={Boolean(errors.value)}
          helperText={errors.value?.message}
          label="Value"
          margin="dense"
          size="small"
          fullWidth
        />
      </>
    );
  };

  if (loadingFeatureType) {
    // FIXME: workaround for react-use-form not resetting controlled fields in a dialog
    return <LoadingIndicator />;
  }

  return (
    <Dialog open={true}>
      {loadingFeatureType ? (
        <LoadingIndicator />
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>{`${featureType ? 'Edit' : 'Add'} Feature Type`}</DialogTitle>
          <DialogContent>
            {renderContent()}
            {createError || updateError ? (
              <Alert sx={{ mt: '1rem' }} severity="error">
                {createError?.response?.data?.message || updateError?.response?.data?.message}
              </Alert>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button disabled={isSubmitting} variant="outlined" onClick={() => navigate('..')} color="primary">
              Cancel
            </Button>
            <LoadingButton
              color="primary"
              type="submit"
              variant="contained"
              loading={isSubmitting || Boolean(gettingError)}
            >
              {featureType ? 'Save' : 'Create'}
            </LoadingButton>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
};
